// src/viqai/AppHomeVIQ.jsx
// ─── Nouvel accueil de user.vehicleiq.fr (§4, §17, §23) ─────────────────────
// Deux blocs, rien d'autre : VIQ.AI à 70 %, bloc-notes à 30 %. Les KPI, les
// objectifs, la liste des derniers dossiers, les gros boutons et la barre de
// recherche disparaissent de cet écran — ils restent accessibles ailleurs et,
// surtout, interrogeables depuis la conversation.
//
// L'ancien `AppHome` n'est pas supprimé : il reste monté derrière un
// interrupteur (voir INTEGRATION.md) le temps que VIQ.AI couvre tout.

import { useState, useEffect, useCallback } from "react";
import VIQAIWorkspace from "./VIQAIWorkspace";
import { apiGet, apiPost } from "./api";
import { registerViqShell } from "./modal-manager";
import "./viqai-dark.css";

const FILTERS = [
  { id: "all", label: "Toutes" },
  { id: "appel", label: "Appels" },
  { id: "email", label: "Email" },
  { id: "rdv", label: "RDV" },
];

// ── Bloc-notes ──────────────────────────────────────────────────────────────
// La logique de notes existante est conservée (mêmes endpoints `/api/crm/notes`)
// mais l'affichage est allégé : le panneau actuel était trop massif pour 30 %.
function NotesWorkspace({ open, onClose, onOpenNote, refreshKey, isCompact = false, onNavigate }) {
  const [notes, setNotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  // Les compteurs viennent du bootstrap VIQ.AI, déjà appelé au montage : pas
  // de requête supplémentaire pour deux nombres.
  const [counts, setCounts] = useState({ leads: 0, agenda: 0 });
  useEffect(() => {
    apiGet("/api/viqai/bootstrap")
      .then(j => { if (j?.success) setCounts({ leads: j.counts?.new_leads || 0, agenda: j.counts?.agenda_today || 0 }); })
      .catch(() => {});
  }, [refreshKey]);

  const load = useCallback(() => {
    setLoading(true);
    apiGet("/api/crm/notes")
      .then(j => setNotes(j?.success ? (j.notes || []) : []))
      .catch(() => setNotes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const add = async () => {
    const title = draft.trim();
    if (!title) return;
    setDraft("");
    await apiPost("/api/crm/notes", { title, action: "note" }).catch(() => {});
    load();
  };

  const visible = notes.filter(n => filter === "all" || n.action === filter);
  const now = Date.now();

  // Regroupement par jour, calculé une fois : un bloc-notes plat ne se lit pas.
  const groups = visible.slice(0, 30).reduce((acc, n) => {
    const d = new Date(n.occurred_at || n.created_at);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.floor((today.getTime() - new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) / 86400000);
    const label = diff <= 0 ? "Aujourd'hui" : diff === 1 ? "Hier" : d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
    (acc[label] = acc[label] || []).push(n);
    return acc;
  }, {});

  return (
    <aside className={`viq-notes${open ? " open" : ""}`} aria-label="Bloc-notes">
      {/* §16-19, §66-67 — Rappel compact. Une ligne, pas une carte : le
          professionnel doit voir ce qui l'attend sans que le bloc-notes se
          transforme en second tableau de bord. Chaque compteur mène
          directement à sa destination, jamais à la vue d'ensemble. */}
      {(counts.leads > 0 || counts.agenda > 0) && (
        <div className="viq-notes-alert">
          {counts.leads > 0 && (
            <button type="button" onClick={() => onNavigate && onNavigate("leads")}>
              <span className="dot" aria-hidden="true">●</span>
              {counts.leads} nouveau{counts.leads > 1 ? "x" : ""} lead{counts.leads > 1 ? "s" : ""}
            </button>
          )}
          {counts.leads > 0 && counts.agenda > 0 && <span aria-hidden="true">·</span>}
          {counts.agenda > 0 && (
            <button type="button" onClick={() => onNavigate && onNavigate("agenda")}>
              {counts.agenda} action{counts.agenda > 1 ? "s" : ""} aujourd'hui
            </button>
          )}
          <span className="arrow" aria-hidden="true">→</span>
        </div>
      )}

      <div className="viq-notes-head">
        <span className="viq-notes-title">Bloc-notes</span>
        {/* §48 — La croix n'apparaît que dans le tiroir mobile. Sur desktop,
            fermer une colonne permanente n'a pas de sens et laisserait un
            espace vide impossible à récupérer sans recharger. */}
        {isCompact && (
          <button type="button" className="viq-tool-btn" onClick={onClose} aria-label="Fermer le bloc-notes">✕</button>
        )}
      </div>

      <input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") add(); }}
        placeholder="+ Nouvelle note"
        aria-label="Nouvelle note"
        style={{
          width: "100%", boxSizing: "border-box", background: "var(--viq-surface-2)",
          border: "1px solid var(--viq-border)", borderRadius: 9, padding: ".5rem .65rem",
          color: "var(--viq-text)", font: "inherit", fontSize: ".84rem",
        }}
      />

      <div className="viq-notes-filters">
        {FILTERS.map(f => (
          <button key={f.id} type="button" className="viq-filter"
            aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>{f.label}</button>
        ))}
      </div>

      {loading && <div className="viq-empty">Chargement…</div>}
      {!loading && !visible.length && (
        <div className="viq-empty">
          Rien de noté. Dites à VIQ.AI « note que je dois rappeler Autopôle demain » — la note apparaîtra ici.
        </div>
      )}

      {Object.entries(groups).map(([label, list]) => (
        <div key={label}>
          <div className="viq-day-label">{label}</div>
          {list.map(n => {
            const due = n.follow_up_at && new Date(n.follow_up_at).getTime() <= now;
            return (
              <div key={n.id} className={`viq-note${due ? " due" : ""}`} onClick={() => onOpenNote && onOpenNote(n)}>
                <div className="viq-note-title">{n.title}</div>
                <div className="viq-note-meta">
                  {new Date(n.occurred_at || n.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                  {n.contact_name ? ` · ${n.contact_name}` : ""}
                  {due ? " · à relancer" : ""}
                  {n.shared_by ? ` · ${n.shared_by}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </aside>
  );
}

// ── Accueil ─────────────────────────────────────────────────────────────────
export default function AppHomeVIQ({
  user, weather, city,
  onOpenDossier, onOpenInterface, onNavigate, onOpenNote,
  isAdmin = false, darkMode, onToggleTheme, onLogout, onLegal,
}) {
  // §6 — Le sombre est le défaut de l'espace utilisateur ; le clair reste
  // disponible dans les paramètres. La préférence existante (`viq_theme`) est
  // relue telle quelle : un utilisateur qui avait déjà choisi le clair le garde.
  // §21 — Le thème vient d'App.jsx et change instantanément : lire
  // localStorage au montage produisait un état figé qui ne se rafraîchissait
  // qu'au rechargement de la page.
  const dark = darkMode !== undefined
    ? darkMode
    : (() => { try { return localStorage.getItem("viq_theme") !== "light"; } catch (e) { return true; } })();
  // §50 — Deux états distincts. Partager un seul booléen faisait disparaître le
  // bloc-notes sur desktop parce que l'utilisateur l'avait fermé sur mobile.
  // Sur grand écran il est structurellement visible, sans état du tout.
  const [notesDrawerOpen, setNotesDrawerOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false);
  // §2, §6 — Le scroll du document est neutralisé pendant que l'espace VIQ.AI
  // est monté, et RESTITUÉ au démontage. Le poser globalement dans une feuille
  // de style bloquerait le site public, qui a besoin de défiler.
  // Le verrou de scroll passe par le gestionnaire : il n'est effectif que
  // lorsque aucune modale n'est ouverte, sans quoi une modale plus haute que
  // l'écran devient impossible à faire défiler (écran « figé »).
  useEffect(() => {
    registerViqShell(true);
    return () => registerViqShell(false);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const onChange = (e) => { setIsCompact(e.matches); if (!e.matches) setNotesDrawerOpen(false); };
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange); };
  }, []);
  const [notesKey, setNotesKey] = useState(0);

  // VIQ.AI peut écrire dans le bloc-notes : il doit se rafraîchir quand elle le
  // fait, sans que l'utilisateur ait à recharger.
  useEffect(() => {
    const bump = () => setNotesKey(k => k + 1);
    window.addEventListener("viq-notes-changed", bump);
    return () => window.removeEventListener("viq-notes-changed", bump);
  }, []);

  const weatherLabel = weather?.temp != null ? `${Math.round(weather.temp)}°` : null;

  return (
    <div className={`viq-workspace ${dark ? "viq-dark" : "viq-light"}`}>
      <VIQAIWorkspace
        user={user}
        weather={weatherLabel}
        city={city || weather?.city || null}
        onOpenDossier={onOpenDossier}
        onOpenInterface={onOpenInterface}
        onNavigate={onNavigate}
        isAdmin={isAdmin}
        darkMode={dark}
        onToggleTheme={onToggleTheme}
        onLogout={onLogout}
        onLegal={onLegal}
        showNotesToggle={isCompact}
        onToggleNotes={() => setNotesDrawerOpen(o => !o)}
      />
      <NotesWorkspace
        open={!isCompact || notesDrawerOpen}
        isCompact={isCompact}
        onClose={() => setNotesDrawerOpen(false)}
        onOpenNote={onOpenNote}
        onNavigate={onNavigate}
        refreshKey={notesKey}
      />
    </div>
  );
}

export { NotesWorkspace };
